import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { string } from "yup";
import { useSettingsStore } from "@/stores/settingsStore";

dayjs.extend(customParseFormat);

export type DateTranslateFn = (key: string, params?: Record<string, unknown>) => string;

/**
 * Value `DateFieldValidator` parks unparseable text under, so a schema run sees "bad", not
 * "empty". Without it a rejected entry left the field value blank, and the schema answered
 * `required` — replacing the specific message the field had just shown.
 */
export const INVALID_DATE_VALUE = "__invalid_date__";

/** Used only when no store is available; the store's own default is `displayFormats[0]`. */
export const FALLBACK_DISPLAY_FORMAT = "MM/DD/YYYY";

/** A bound, either fixed or resolved per validation (`values.end_date`, `tomorrow`, …). */
export type DateBound = string | Date | (() => string | Date | null | undefined) | null | undefined;

const resolveBound = (bound: DateBound) => {
  const raw = typeof bound === "function" ? bound() : bound;
  if (!raw) return null;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed : null;
};

/** Parsed non-strictly on purpose — the strict format check already happened in the field. */
const parseDate = (value: string) => dayjs(value);

/**
 * Resolved per validation, not when the schema is built: the store is a singleton that may not
 * exist yet at module load, and reading it late means a preference changed mid-session still
 * lands in the next message. A bound formatted `MM/DD/YYYY` next to a field the user types as
 * `DD/MM/YYYY` reads as the wrong date entirely.
 */
const resolveDisplayFormat = (override?: string) => {
  if (override) return override;
  try {
    return useSettingsStore().dateFormatDisplay;
  } catch {
    // No active pinia (a schema validated outside the app, e.g. in a unit test).
    return FALLBACK_DISPLAY_FORMAT;
  }
};

export type DateSchemaOptions = {
  /** Field label for the `required` message. Pass the same key the field renders. */
  fieldKey?: string;
  required?: boolean;
  /** Earliest accepted date; omit for no lower bound. */
  min?: DateBound;
  /** Latest accepted date; omit for no upper bound. */
  max?: DateBound;
  /** Overrides the default "Date must be on or after {date}" for this field. */
  minMessage?: (formatted: string) => string;
  /** Overrides the default "Date must be on or before {date}" for this field. */
  maxMessage?: (formatted: string) => string;
  /**
   * Display format for the boundary dates inside the messages. Defaults to the user's
   * `dateFormatDisplay` preference, matching what `DateFieldValidator` renders.
   */
  displayFormat?: string;
};

/**
 * Date rules as Yup, so the message the user sees survives a form-level validation run.
 *
 * `DateFieldValidator` checks parseability and its `min`/`max` locally via `setErrors`, but any
 * schema run — a submit, or a `validate()` on mount — rewrites the whole error bag. A schema
 * holding only `required()` therefore replaced "Invalid date" / "Date must be on or after …"
 * with "<Field> is a required field" the moment the user hit save.
 *
 * Mirroring the field's own checks here keeps both paths on the same verdict. Bounds accept a
 * getter so a schema built once can still track a bound that moves (a sibling field, "today").
 */
export const dateValidationSchema = (
  t: DateTranslateFn,
  {
    fieldKey = "generic.fields.date",
    required = false,
    min,
    max,
    minMessage,
    maxMessage,
    displayFormat,
  }: DateSchemaOptions = {}
) => {
  let schema = string().nullable();

  if (required) {
    schema = schema.required(t("generic.errors.fieldIsRequiredField", { field: t(fieldKey) }));
  }

  // Parseability first: a garbage string must not be answered with a bound it cannot satisfy.
  schema = schema.test("valid-date", t("generic.errors.invalidDate"), (value) => {
    if (!value) return true;
    if (value === INVALID_DATE_VALUE) return false;
    return parseDate(value).isValid();
  });

  // Bounds resolve inside the test, not at build time, so a getter stays live. The message does
  // too — Yup accepts a function and calls it with the failing test's params.
  if (min !== undefined) {
    schema = schema.test({
      name: "min-date",
      test(value) {
        if (!value || value === INVALID_DATE_VALUE) return true;
        const parsed = parseDate(value);
        if (!parsed.isValid()) return true;

        const bound = resolveBound(min);
        if (!bound) return true;
        if (!parsed.startOf("day").isBefore(bound.startOf("day"))) return true;

        const formatted = bound.format(resolveDisplayFormat(displayFormat));
        return this.createError({
          message: minMessage
            ? minMessage(formatted)
            : t("generic.errors.dateMustBeAfter", { date: formatted }),
        });
      },
    });
  }

  if (max !== undefined) {
    schema = schema.test({
      name: "max-date",
      test(value) {
        if (!value || value === INVALID_DATE_VALUE) return true;
        const parsed = parseDate(value);
        if (!parsed.isValid()) return true;

        const bound = resolveBound(max);
        if (!bound) return true;
        if (!parsed.startOf("day").isAfter(bound.endOf("day"))) return true;

        const formatted = bound.format(resolveDisplayFormat(displayFormat));
        return this.createError({
          message: maxMessage
            ? maxMessage(formatted)
            : t("generic.errors.dateMustBeBefore", { date: formatted }),
        });
      },
    });
  }

  return schema;
};
